import { useEffect, useRef, useState } from "react"
import { Mic, Loader2, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition"

interface AssistenteVozContextualProps {
  onTranscricao: (texto: string, contexto: string | null) => Promise<string | null>
  processando: boolean
  className?: string
}

export function AssistenteVozContextual({
  onTranscricao,
  processando,
  className,
}: AssistenteVozContextualProps) {
  const speech = useSpeechRecognition()
  const [aberto, setAberto] = useState(false)
  const [respostaIA, setRespostaIA] = useState<string | null>(null)
  const ultimoTextoRef = useRef("")
  const ultimaTranscricaoRef = useRef<string | null>(null)

  useEffect(() => {
    const texto = speech.transcript || speech.interimTranscript
    if (texto) ultimoTextoRef.current = texto
  }, [speech.transcript, speech.interimTranscript])

  useEffect(() => {
    if (!aberto) return
    if (speech.state !== "idle") return
    const texto = ultimoTextoRef.current.trim()
    if (!texto) return

    // Contexto acumulado: o que o usuário disse + o que a IA respondeu
    const contexto = (ultimaTranscricaoRef.current && respostaIA)
      ? `Usuário disse: "${ultimaTranscricaoRef.current}". Assistente respondeu: "${respostaIA}"`
      : respostaIA

    ultimaTranscricaoRef.current = texto
    ultimoTextoRef.current = ""
    void onTranscricao(texto, contexto).then((mensagem) => {
      setAberto(false)
      speech.reset()
      if (mensagem) setRespostaIA(mensagem)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speech.state, aberto])

  const iniciar = () => {
    ultimoTextoRef.current = ""
    speech.reset()
    setAberto(true)
    speech.start()
  }

  const cancelar = () => {
    speech.stop()
    speech.reset()
    ultimoTextoRef.current = ""
    setAberto(false)
  }

  if (processando) {
    return (
      <div className={cn("fixed bottom-6 right-6 z-50", className)}>
        <div className="flex items-center gap-2 rounded-full bg-primary px-4 py-3 text-primary-foreground shadow-lg">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm font-medium">Processando...</span>
        </div>
      </div>
    )
  }

  if (aberto) {
    return (
      <div className={cn("fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3", className)}>
        {(speech.transcript || speech.interimTranscript) && (
          <div className="max-w-xs rounded-lg bg-background/95 p-3 text-sm shadow-lg ring-1 ring-border backdrop-blur">
            {speech.transcript || speech.interimTranscript}
          </div>
        )}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={cancelar}
            className="h-10 w-10 rounded-full p-0 shadow-lg"
            aria-label="Cancelar"
          >
            <X className="h-4 w-4" />
          </Button>
          <button
            onClick={speech.stop}
            aria-label="Parar gravação"
            className="flex h-16 w-16 items-center justify-center rounded-full bg-red-600 shadow-lg transition-transform hover:scale-105 animate-pulse"
          >
            <Mic className="h-7 w-7 text-white" />
          </button>
        </div>
        <p className="text-xs text-muted-foreground">Ouvindo... toque para parar</p>
      </div>
    )
  }

  if (!speech.isSupported) return null

  // Card de resposta da IA + botão mic
  if (respostaIA) {
    return (
      <div className={cn("fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3", className)}>
        <div className="max-w-sm rounded-xl bg-background/95 p-4 shadow-lg ring-1 ring-border backdrop-blur space-y-3">
          <p className="text-sm leading-relaxed">{respostaIA}</p>
          <div className="flex gap-2">
            <Button size="sm" onClick={iniciar} className="flex-1 gap-2">
              <Mic className="h-4 w-4" />
              Falar de novo
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRespostaIA(null)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn("fixed bottom-6 right-6 z-50", className)}>
      <button
        onClick={iniciar}
        aria-label="Assistente de voz"
        className="flex h-14 w-14 items-center justify-center rounded-full bg-primary shadow-lg transition-transform hover:scale-105"
      >
        <Mic className="h-6 w-6 text-primary-foreground" />
      </button>
    </div>
  )
}
